import React from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';

import LocationOn from '@mui/icons-material/LocationOn';
import Restaurant from '@mui/icons-material/Restaurant';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import DateRangeIcon from '@mui/icons-material/DateRange';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MedicalServices from '@mui/icons-material/MedicalServices';

import {
  Button,
  useMediaQuery,
} from '@mui/material';
import { uniq } from 'lodash';
import { Share } from '@mui/icons-material';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import HealthSection from './components/HealthSection';
import TasksCard from './components/TasksCard';
import DataCard from './components/DataCard';
import FeedsCard from './components/FeedsCard';
import LocationsCard from './components/LocationsCard';
import NotesCard from './components/NotesCard';
import ProfileCard from './components/ProfileCard';
import SchedulesCard from './components/SchedulesCard';
import HorseAccessCard from './components/HorseAccessCard';
import PublicShareLinksCard from './components/PublicShareLinksCard';

import HorseService from '../../../services/HorseService';

import {
  getHorse,
  getLodgingsForHorse,
  getAllLodgingsForHorse,
  getHorseLocations,
  getHorseSuggestion,
} from '../../../redux/horse/actions';
import { getHorseAccess } from '../../../redux/horseAccess/actions';
import { requestAccessForHorse } from '../../../redux/requests/actions'
import {
  getQueryParam,
  setQueryParam,
} from '../../../utils/urlHelper';
import AddEditModal from './components/NotesCard/components/AddEditModal';
import HorseIcon from '../../../components/CustomIcons/Horse';
import NoDataIcon from '../../../images/unused-undraw-illustrations/undraw_no_data_re_kwbl.svg';
import NavigationBarnComponent from './components/NavigationBar';
import theme from '../../../utils/theme';
import { primary } from '../../../utils/brandColors';
import AddHorseModalComponent from '../Horses/components/AddHorseModal';

const HorsePageComponent = ({
  navigate,
  permissions,
  unreadCount,
  chatClient,
  pageWrapper,
  id: propId,
  tab: propTab,
  condensed,
}) => {
  const dispatch = useDispatch();
  const { id: paramId } = useParams();
  const id = paramId || propId;
  if (!id) { navigate('/horses'); }

  const fullscreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [open, setOpen] = React.useState(!fullscreen);
  const [editProfile, setEditProfile] = React.useState(false)

  React.useEffect(() => {
    setOpen(!fullscreen)
  }, [fullscreen])

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const horse = useSelector((state) => state.horse.horse);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);
  const allHorseLodgings = useSelector((state) => state.horse.allHorseLodgings);
  const horseLocations = useSelector((state) => state.horse.horseLocations);
  const isFetchingHorse = useSelector((state) => state.horse.isFetchingHorse);
  const isFetchingHorseLodgings = useSelector((state) => state.horse.isFetchingHorseLodgings);
  const isFetchingAllHorseLodgings = useSelector((state) => state.horse.isFetchingAllHorseLodgings);
  const horseAccess = useSelector((state) => state.horseAccess.horseAccess);
  const isLoading = isFetchingAllHorseLodgings || isFetchingHorseLodgings || isFetchingHorse;

  // React state
  const [tab, setTab] = React.useState(propTab || 0);
  const [isAddingHorse, setIsAddingHorse] = React.useState(false);
  const [openRequestAccessModal, setOpenRequestAccessModal] = React.useState(false)
  const [selectedRequestBarn, setSelectedRequestBarn] = React.useState(null)
  const [note, setNote] = React.useState('');
  const [showPopupMessage, setShowPopupMessage] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState({
    open: false,
    snackPack: [],
    messageInfo: null,
    severity: 'success',
  });
  const [openAddEditModal, setOpenAddEditModal] = React.useState(false);
  const [noteModalCallback, setNoteModalCallback] = React.useState(() => { });
  const [noteModalNoteType, setNoteModalNoteType] = React.useState('');
  const [selectedNote, setSelectedNote] = React.useState(null);
  const [currentHorseLocations, setCurrentLocations] = React.useState([]);
  const [horseOwners, setHorseOwners] = React.useState([]);

  React.useEffect(() => {
    setEditProfile(false);
    dispatch(getHorse(id));
    dispatch(getLodgingsForHorse(id, selectedBarn.id));
    dispatch(getHorseLocations(id, 0, 5, '&sort=startDate,desc'));
    dispatch(getHorseAccess(id, 0, 15, '&sort=endDate,desc'));
    dispatch(getHorseSuggestion(id));
  }, [id]);

  React.useEffect(() => {
    const queryParamTab = getQueryParam('tab')
      ? parseInt(getQueryParam('tab'), 10)
      : 0;
    setTab(queryParamTab);
    if (!condensed) {
      setQueryParam('tab', queryParamTab);
    }
  }, []);

  React.useEffect(() => {
    // find current horse location
    if (horseLocations.content.length > 0) {
      const now = new Date().getTime();
      const sublist = horseLocations.content.filter((l) => new Date(l.startDate).getTime() < now
        && (l.endDate === null || new Date(l.endDate).getTime() > now));
      setCurrentLocations(sublist.length > 0
        ? sublist
        : null);
    } else {
      setCurrentLocations([]);
    }
  }, [horseLocations]);

  React.useEffect(() => {
    // find current horse owners
    if (horseAccess.content.length > 0) {
      setHorseOwners(horseAccess.content.filter((ha) => ha.owner));
    } else {
      setHorseOwners([]);
    }
  }, [horseAccess]);

  const handleChangeTab = (event, newValue) => {
    setTab(newValue);
    if (!condensed) {
      setQueryParam('tab', newValue);
    }
  };

  const onConfirmRequestHorse = () => {
    setIsAddingHorse(true)
    dispatch(requestAccessForHorse(selectedRequestBarn.id, id, {
      lodgingStartDate: new Date(),
      ownership: 0,
      requestNote: note,
    })).then(() => {
      setIsAddingHorse(false)
      setShowPopupMessage(true)
      setOpenRequestAccessModal(false);
      setMessageInfo({
        ...messageInfo,
        severity: 'success',
        message: `The request was sent to ${selectedRequestBarn.name}. You will be notified once it has been accepted or rejected.`,
      })
    }).catch((error) => {
      console.error(error);
      console.error(error.message);
      if (error && error.message && error.message.indexOf('Access to this horse has already been requested') >= 0) {
        setOpenRequestAccessModal(false);
        setIsAddingHorse(false)
        setShowPopupMessage(true)
        setMessageInfo({
          ...messageInfo,
          severity: 'warning',
          message: 'Access to this horse has already been requested.',
        })
      } else {
        setOpenRequestAccessModal(false);
        setIsAddingHorse(false)
        setShowPopupMessage(true)
        setMessageInfo({
          ...messageInfo,
          severity: 'error',
          message: 'There was an error trying to send this request. If the issue persists, please contact Questri support.',
        })
      }
    });
  }

  const handleOpenAddEditModal = (passedInNote, noteType, callback) => {
    setOpenAddEditModal(true);
    setSelectedNote(passedInNote);
    setNoteModalNoteType(noteType);
    setNoteModalCallback(callback);
  }
  const handleCloseAddEditModal = () => {
    if (noteModalCallback != null) {
      noteModalCallback();
    }
    setOpenAddEditModal(false);
    setSelectedNote(null);
    setNoteModalNoteType('');
    setNoteModalCallback(null);
  };

  const handleAddHorse = () => {
    setIsAddingHorse(true)
    HorseService.updateHorse(id, {
      lodgeHorseWithBarn: true,
      lodgingStartDate: new Date(),
      lodgingNote: note,
      lodgingInactive: false,
    }).then(() => {
      setIsAddingHorse(false);
      dispatch(getLodgingsForHorse(id, selectedBarn.id));
      dispatch(getAllLodgingsForHorse(id, 0, 10, '&sort=createdTimestamp,desc'));
    })
  }

  const barnHasAccessToHorse = React.useMemo(() => (currentHorseLodging && currentHorseLodging.id), [currentHorseLodging]);
  const currentBarnsLodgingHorse = React.useMemo(() => (uniq(allHorseLodgings.content.filter((hl) => hl.endDate == null
    || (new Date(hl.startDate).getTime() < new Date().getTime() && (!hl.endDate || new Date(hl.endDate).getTime() > new Date().getTime()))), 'barn.id')), [allHorseLodgings]);

  const HorseComponent = (
    <Box
      sx={{
        flexGrow: 1,
        backgroundColor: condensed
          ? 'rgb(244, 244, 244)'
          : undefined,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ProfileCard
            id={id}
            navigate={navigate}
            openAddEditNoteModal={handleOpenAddEditModal}
            currentHorseLocations={currentHorseLocations || []}
            horseOwners={horseOwners}
            setTab={setTab}
            openEditMode={editProfile}
            setOpenEditMode={setEditProfile}
            condensed={condensed}
          />
        </Grid>
        {
          (isLoading || barnHasAccessToHorse)
            ? (
              <Grid item xs={12}>
                <Tabs
                  value={tab}
                  onChange={handleChangeTab}
                  aria-label="horse profile tab selection"
                  variant="scrollable"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Tab value={0} icon={<Box><HorseIcon color={primary[500]} /></Box>} iconPosition="start" label="Profile" />
                  <Tab value={1} icon={<NoteAltIcon color="primary" />} iconPosition="start" label="Notes" />
                  <Tab value={2} icon={<MedicalServices color="primary" />} iconPosition="start" label="Health" />
                  <Tab value={3} icon={<Restaurant color="primary" />} iconPosition="start" label="Feed" />
                  <Tab value={4} icon={<LocationOn color="primary" />} iconPosition="start" label="Locations" />
                  {(permissions.owner || permissions.admin) && <Tab value={5} icon={<GroupIcon color="primary" />} iconPosition="start" label="Users & Access" />}
                  {!condensed && <Tab value={6} icon={<AssignmentIcon color="primary" />} iconPosition="start" label="Tasks" />}
                  {!condensed && <Tab value={7} icon={<DateRangeIcon color="primary" />} iconPosition="start" label="Calendar" />}
                  {(permissions.owner || permissions.admin) && <Tab value={8} icon={<Share color="primary" />} iconPosition="start" label="Share Links" />}
                </Tabs>
              </Grid>
            )
            : (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" align="center" component="div" sx={{ m: 2 }}>
                    No Access To Horse
                  </Typography>
                  <Typography align="center" component="div" sx={{ mb: 4 }}>
                    Your Questri Barn does not have access to this horse. Notes, tasks, and other information
                    about the horse is restricted unless it is added to your organization.
                    <Box sx={{ mt: 0.5 }}>
                      {
                        currentBarnsLodgingHorse.length === 0
                          ? 'You can add the horse below.'
                          : 'You can request access from a Questri Barn that currently has it added.'
                      }
                    </Box>
                  </Typography>
                  {
                    currentBarnsLodgingHorse.length === 0
                      ? (
                        <LoadingButton disabled={isAddingHorse} loading={isAddingHorse} size="large" variant="contained" onClick={handleAddHorse}>Add Horse To {selectedBarn
                          ? selectedBarn.name
                          : ''}
                        </LoadingButton>
                      )
                      : (
                        <Box
                          sx={{
                            textAlign: 'center',
                            maxWidth: 600,
                            margin: 'auto',
                          }}
                        >
                          <List
                            component="div"
                            sx={{
                              maxHeight: 400,
                              maxWidth: 600,
                              scroll: 'auto',
                            }}
                          >
                            <ListSubheader>Questri Barns Currently With Access</ListSubheader>
                            {currentBarnsLodgingHorse.map((hl) => (
                              <ListItem
                                key={`lodging-barn-${hl.id}`}
                                button
                                onClick={() => { setSelectedRequestBarn(hl.barn); setOpenRequestAccessModal(true) }}
                                disablePadding
                              >
                                <ListItemButton>
                                  <ListItemIcon>
                                    <Avatar src={`https://d8adkpqcrx2wo.cloudfront.net/${hl.barn.tenant}.png`} />
                                  </ListItemIcon>
                                  <ListItemText primary={hl.barn.name} secondary="You can request access from any barn that currently has access to it in Questri" />
                                  <Button variant="contained" size="small" onClick={() => { setSelectedRequestBarn(hl.barn); setOpenRequestAccessModal(true) }}>Request Access</Button>
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )
                  }
                  <img
                    src={NoDataIcon}
                    alt="Empty clipboards"
                    style={{
                      width: 300,
                      display: 'block',
                      margin: 'auto',
                      marginTop: '4rem',
                      marginBottom: '1rem',
                    }}
                  />
                </Box>
              </Grid>
            )
        }
        {(barnHasAccessToHorse && tab === 0) && (
          <Grid item xs={12}>
            <DataCard
              id={id}
              onNotesNavigate={(e) => { handleChangeTab(e, 1) }}
              openAddEditNoteModal={handleOpenAddEditModal}
              handleEditProfile={() => { setEditProfile(true); }}
              condensed={condensed}
            />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 1) && (
          <Grid item xs={12}>
            <NotesCard id={id} isAdmin={permissions.owner || permissions.admin} openAddEditNoteModal={handleOpenAddEditModal} condensed={condensed} />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 2) && (
          <Grid item xs={12}>
            <HealthSection
              id={id}
              isAdmin={permissions.owner || permissions.admin}
              openAddEditNoteModal={handleOpenAddEditModal}
              condensed={condensed}
            />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 3) && (
          <Grid item xs={12}>
            <FeedsCard id={id} condensed={condensed} />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 4) && (
          <Grid item xs={12}>
            <LocationsCard id={id} condensed={condensed} />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 5 && (permissions.owner || permissions.admin)) && (
          <Grid item xs={12}>
            <HorseAccessCard id={id} isAdmin={permissions.owner || permissions.admin} condensed={condensed} />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 6) && (
          <Grid item xs={12}>
            {(currentHorseLodging && currentHorseLodging.pingumTagId)
              ? (
                <TasksCard
                  navigate={navigate}
                  id={id}
                  permissions={permissions}
                  setTab={setTab}
                  condensed={condensed}
                />
              )
              : 'No horse selected...'}
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 7) && (
          <Grid item xs={12}>
            <SchedulesCard id={id} condensed={condensed} />
          </Grid>
        )}
        {(barnHasAccessToHorse && tab === 8 && (permissions.owner || permissions.admin)) && (
          <Grid item xs={12}>
            <PublicShareLinksCard id={id} isAdmin={permissions.owner || permissions.admin} condensed={condensed} />
          </Grid>
        )}
      </Grid>
      <AddEditModal
        open={openAddEditModal}
        horseNote={selectedNote}
        presetType={noteModalNoteType}
        horseId={id}
        pinnedHorseNoteId={(id && horse.activeNote)
          ? horse.activeNote.id
          : null}
        onClose={handleCloseAddEditModal}
      />
      <AddHorseModalComponent
        horse={horse}
        navigate={navigate}
        open={editProfile}
        onClose={() => { setEditProfile(false); dispatch(getHorse(id)); }}
      />
      <Snackbar
        key={messageInfo
          ? messageInfo.key
          : undefined}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={showPopupMessage}
        autoHideDuration={10000}
        onClose={() => setShowPopupMessage(false)}
        message={messageInfo
          ? messageInfo.message
          : undefined}
      >
        <Alert variant="filled" onClose={() => setShowPopupMessage(false)} severity={messageInfo.severity} sx={{ width: '100%' }}>
          {messageInfo
            ? messageInfo.message
            : undefined}
        </Alert>
      </Snackbar>

      <Modal
        open={openRequestAccessModal}
        onClose={() => setOpenRequestAccessModal(false)}
        aria-labelledby="modal-horse-request-access"
        aria-describedby="modal-horse-request-access"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            pt: 2,
            px: 4,
            pb: 3,
          }}
        >
          <Typography id="modal-request-access-title" variant="h6" component="h2" gutterBottom>
            {`Request Access To ${(horse && horse.name) || 'Unknown'}`}
          </Typography>
          <TextField
            sx={{
              mt: 2,
              mb: 2,
            }}
            label="Access Request Note"
            fullWidth
            disabled={isAddingHorse}
            onChange={(e) => { setNote(e.target.value); }}
            helperText="Leave a description or note about this access request for the Questri org being requested"
          />
          <LoadingButton
            sx={{
              width: '100%',
              mt: 2,
            }}
            onClick={() => onConfirmRequestHorse((currentHorseLodging && currentHorseLodging.id))}
            variant="contained"
            disabled={isAddingHorse}
            loading={isAddingHorse}
          >Confirm And Request
          </LoadingButton>
        </Box>
      </Modal>
    </Box>
  )

  if (!pageWrapper) {
    return HorseComponent;
  }

  return (
    <Page>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
        }}
      >
        <NavigationBarnComponent navigate={navigate} open={open} setOpen={setOpen} />
        <PageBody
          navigate={navigate}
          chatClient={chatClient}
          unreadCount={unreadCount}
          width={`calc(100% - ${open
            ? 225
            : 56}px)`}
        >
          {HorseComponent}
        </PageBody>
      </Box>
    </Page>
  );
};

HorsePageComponent.propTypes = {
  navigate: PropTypes.func.isRequired,
  permissions: PropTypes.object.isRequired,
  chatClient: PropTypes.any,
  unreadCount: PropTypes.number,
  pageWrapper: PropTypes.bool,
  id: PropTypes.string,
  tab: PropTypes.number,
  condensed: PropTypes.bool,
};
HorsePageComponent.defaultProps = {
  chatClient: null,
  unreadCount: 0,
  pageWrapper: true,
  id: null,
  tab: 0,
  condensed: false,
};
export default HorsePageComponent;
